import { useState, type CSSProperties, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ChorePriority } from '@chore-project/shared';
import { ChoreTile } from '../components/ChoreTile';
import { tapLight } from '../lib/haptics';
import './design-lab.css';

/**
 * /design-lab — unlisted review page for chore-row concepts.
 * Mock data only (no auth, no API); every check is tappable (tap again to
 * un-complete) so each concept's completion moment can be felt on-device.
 * Follows the system light/dark theme like the rest of the app.
 */

interface MockChore {
  id: string;
  name: string;
  room?: string;
  length: string;
  due: string;
  late?: boolean;
  pts: number;
  priority: ChorePriority;
}

const CHORES: MockChore[] = [
  { id: 'c1', name: 'Washing machine', room: 'Kitchen', length: '15–30 min', due: '1 day late', late: true, pts: 4, priority: ChorePriority.MUST_DO },
  { id: 'c2', name: 'Hoover', length: '5–10 min', due: '1 day late', late: true, pts: 3, priority: ChorePriority.SHOULD_DO },
  { id: 'c3', name: 'Mow the lawn', room: 'Garden', length: '45+ min', due: 'Today', pts: 6, priority: ChorePriority.SHOULD_DO },
  { id: 'c4', name: 'Litter box', room: 'Kitchen', length: '5–10 min', due: 'Today', pts: 4, priority: ChorePriority.MUST_DO },
  { id: 'c5', name: 'Dust the shelves', room: 'Living room', length: '15–30 min', due: 'Today', pts: 3, priority: ChorePriority.CAN_DO },
  { id: 'c6', name: 'Put clothes away', room: 'Bedroom', length: '5–10 min', due: 'Tomorrow', pts: 3, priority: ChorePriority.SHOULD_DO },
  // Stress test: long chore name + long room name in one row
  { id: 'c7', name: 'Descale the shower head and re-seal the grout around the bath', room: 'Downstairs guest bathroom & utility room', length: '45+ min', due: 'Today', pts: 7, priority: ChorePriority.MUST_DO },
  // Normal-sized neighbour so grid views pair the stress tile with a typical one
  { id: 'c8', name: 'Water the plants', room: 'Living room', length: '5–10 min', due: 'Tomorrow', pts: 2, priority: ChorePriority.CAN_DO },
];

const LATE = CHORES.filter((c) => c.late);
const TODO = CHORES.filter((c) => !c.late);

const RING: Record<ChorePriority, string> = {
  [ChorePriority.MUST_DO]: 'var(--priority-must)',
  [ChorePriority.SHOULD_DO]: 'var(--primary)',
  [ChorePriority.CAN_DO]: 'var(--priority-can)',
};

/** Local completed-set per concept, so concepts can be tried independently. */
function useDoneSet(): [Set<string>, (id: string) => void] {
  const [done, setDone] = useState<Set<string>>(new Set());
  const toggle = (id: string) => {
    tapLight();
    setDone((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  return [done, toggle];
}

/** The app's check-circle, reused so completion reads the same across concepts. */
function Check({ chore, done, onToggle, ring }: { chore: MockChore; done: boolean; onToggle: () => void; ring?: string }) {
  return (
    <button
      className={`chore-check${done ? ' chore-check-done' : ''}`}
      style={{ '--check-ring': ring ?? RING[chore.priority] } as CSSProperties}
      onClick={onToggle}
      aria-label={`Mark ${chore.name} done`}
    >
      <ion-icon name="checkmark-sharp" />
    </button>
  );
}

function Meta({ chore, roomClass = 'dl-room', lateClass = 'dl-late' }: { chore: MockChore; roomClass?: string; lateClass?: string }) {
  return (
    <>
      {chore.room && <span className={roomClass}>{chore.room}</span>}
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}>
        <ion-icon name="timer-outline" />
        {chore.length}
      </span>
      <span className={chore.late ? lateClass : undefined}>{chore.due}</span>
    </>
  );
}

function Concept({ id, title, blurb, children }: { id: string; title: string; blurb: string; children: ReactNode }) {
  return (
    <section id={id} className="dl-concept">
      <h2 className="dl-concept-title">{title}</h2>
      <p className="dl-concept-blurb">{blurb}</p>
      {children}
    </section>
  );
}

function SectionLabel({ danger, children }: { danger?: boolean; children: ReactNode }) {
  return <p className={`dl-label${danger ? ' dl-label--danger' : ''}`}>{children}</p>;
}

/* ============ Reference · current implementation (B1, shipped) ============ */
function ConceptCurrent() {
  const [done, toggle] = useDoneSet();
  const tile = (c: MockChore) => (
    <ChoreTile
      key={c.id}
      title={c.name}
      priority={c.priority}
      room={c.room}
      points={c.pts}
      overdue={c.late && !done.has(c.id)}
      done={done.has(c.id)}
      onComplete={done.has(c.id) ? undefined : () => toggle(c.id)}
      onOpen={() => {}}
      meta={
        <>
          <span>{c.length}</span>
          <span className={c.late ? 'chore-due-late' : undefined}>{c.due}</span>
        </>
      }
    />
  );
  return (
    <>
      <SectionLabel danger>Overdue — {LATE.length}</SectionLabel>
      <div className="chore-tiles">{LATE.map(tile)}</div>
      <SectionLabel>To do — {TODO.length}</SectionLabel>
      <div className="chore-tiles">{TODO.map(tile)}</div>
    </>
  );
}

/* ============ A · Ledger ============ */
function ConceptLedger() {
  const [done, toggle] = useDoneSet();
  const row = (c: MockChore) => (
    <div key={c.id} className={`dl-ledger-row${done.has(c.id) ? ' dl-ledger-row--done' : ''}`}>
      <Check chore={c} done={done.has(c.id)} onToggle={() => toggle(c.id)} />
      <div className="dl-ledger-body">
        <div className="dl-ledger-title">{c.name}</div>
        <div className="dl-ledger-meta"><Meta chore={c} /></div>
      </div>
      <span className="dl-ledger-pts">+{c.pts}</span>
    </div>
  );
  return (
    <div className="dl-ledger">
      <SectionLabel danger>Overdue — {LATE.length}</SectionLabel>
      {LATE.map(row)}
      <SectionLabel>To do — {TODO.length}</SectionLabel>
      {TODO.map(row)}
    </div>
  );
}

/* ============ B2 · Tiles (square) ============ */
function ConceptTilesSquare() {
  const [done, toggle] = useDoneSet();
  const tile = (c: MockChore) => {
    const d = done.has(c.id);
    return (
      <div key={c.id} className={`dl-tile2${c.late && !d ? ' dl-tile2--late' : ''}${d ? ' dl-tile2--done' : ''}`}>
        <span className={`pts-chip dl-tile-pts${d ? ' pts-chip--earned' : ''}`}>+{c.pts}</span>
        <div className="dl-tile-title">{c.name}</div>
        <div className="dl-tile-meta">
          {c.room && <span className="dl-room">{c.room}</span>}
          <span>{c.length}</span>
          <span className={c.late ? 'dl-late' : undefined}>{c.due}</span>
        </div>
        <button className="dl-tile2-check" onClick={() => toggle(c.id)} aria-label={`Mark ${c.name} done`}>
          <ion-icon name={d ? 'checkmark-sharp' : 'checkmark-outline'} />
        </button>
      </div>
    );
  };
  return (
    <>
      <SectionLabel danger>Overdue — {LATE.length}</SectionLabel>
      <div className="dl-tiles">{LATE.map(tile)}</div>
      <SectionLabel>To do — {TODO.length}</SectionLabel>
      <div className="dl-tiles">{TODO.map(tile)}</div>
    </>
  );
}

/* ============ C · Stickers ============ */
function ConceptStickers() {
  const [done, toggle] = useDoneSet();
  const sticker = (c: MockChore) => {
    const d = done.has(c.id);
    return (
      <div key={c.id} className={`dl-sticker${c.late && !d ? ' dl-sticker--late' : ''}${d ? ' dl-sticker--done' : ''}`}>
        <Check chore={c} done={d} onToggle={() => toggle(c.id)} ring="var(--text)" />
        <div className="dl-sticker-body">
          <div className="dl-sticker-title">{c.name}</div>
          <div className="dl-sticker-meta"><Meta chore={c} /></div>
        </div>
        <span className={`pts-chip${d ? ' pts-chip--earned' : ''}`}>+{c.pts}</span>
      </div>
    );
  };
  return (
    <>
      <SectionLabel danger>Overdue — {LATE.length}</SectionLabel>
      <div className="dl-stickers">{LATE.map(sticker)}</div>
      <SectionLabel>To do — {TODO.length}</SectionLabel>
      <div className="dl-stickers">{TODO.map(sticker)}</div>
    </>
  );
}

/* ============ D · Trail ============ */
function ConceptTrail() {
  const [done, toggle] = useDoneSet();
  const item = (c: MockChore) => {
    const d = done.has(c.id);
    return (
      <div key={c.id} className={`dl-trail-item${d ? ' dl-trail-item--done' : ''}`}>
        <Check chore={c} done={d} onToggle={() => toggle(c.id)} />
        <div className="dl-trail-body">
          <div className="dl-trail-title">{c.name}</div>
          <div className="dl-trail-meta"><Meta chore={c} /></div>
        </div>
        <span className={`pts-chip dl-trail-pts${d ? ' pts-chip--earned' : ''}`}>+{c.pts}</span>
      </div>
    );
  };
  return (
    <>
      <SectionLabel danger>Overdue — {LATE.length}</SectionLabel>
      <div className="dl-trail">{LATE.map(item)}</div>
      <SectionLabel>To do — {TODO.length}</SectionLabel>
      <div className="dl-trail">{TODO.map(item)}</div>
    </>
  );
}

/* ============ E · Stripes ============ */
function ConceptStripes() {
  const [done, toggle] = useDoneSet();
  const stripe = (c: MockChore) => {
    const d = done.has(c.id);
    return (
      <div key={c.id} className={`dl-stripe${c.late && !d ? ' dl-stripe--late' : ''}${d ? ' dl-stripe--done' : ''}`}>
        <button className="dl-stripe-zone" onClick={() => toggle(c.id)} aria-label={`Mark ${c.name} done`}>
          <ion-icon name={d ? 'checkmark-sharp' : 'checkmark-outline'} />
        </button>
        <div className="dl-stripe-body">
          <div className="dl-stripe-title">{c.name}</div>
          <div className="dl-stripe-meta"><Meta chore={c} /></div>
        </div>
        <span className="dl-stripe-pts">+{c.pts}</span>
      </div>
    );
  };
  return (
    <div className="dl-stripes">
      <SectionLabel danger>Overdue — {LATE.length}</SectionLabel>
      {LATE.map(stripe)}
      <SectionLabel>To do — {TODO.length}</SectionLabel>
      {TODO.map(stripe)}
    </div>
  );
}

const CONCEPTS = [
  {
    id: 'current', title: 'Current · Tiles (B1)', component: <ConceptCurrent />,
    blurb: 'The shipped implementation (real ChoreTile component): two-column grid, bare points value, room in small caps, check-circle in the foot.',
  },
  {
    id: 'ledger', title: 'A · Ledger', component: <ConceptLedger />,
    blurb: 'No cards at all — full-width rows on the page itself, hairline separators, big typography. Airy and editorial; the check-circle and points carry the colour.',
  },
  {
    id: 'tiles-square', title: 'B2 · Tiles — square', component: <ConceptTilesSquare />,
    blurb: 'Tiles with sharp corners and the tick as a solid square zone in the bottom-right corner — thumb-first placement, zero radius.',
  },
  {
    id: 'stickers', title: 'C · Stickers', component: <ConceptStickers />,
    blurb: 'Hand-placed feel: inked outlines, offset solid shadows, every shape slightly different and slightly tilted. The most playful and least uniform.',
  },
  {
    id: 'trail', title: 'D · Trail', component: <ConceptTrail />,
    blurb: 'The day as a path — no boxes, check-circles joined by a rail that fills green as you go. Progress reads at a glance and completion literally builds the trail.',
  },
  {
    id: 'stripes', title: 'E · Stripes', component: <ConceptStripes />,
    blurb: 'Full-bleed colour blocks with a square solid check zone. Bold, zero radius, maximum status colour; edge-to-edge like native list apps.',
  },
];

export function DesignLabPage() {
  return (
    <div className="dl-page">
      <h1>Design lab</h1>
      <p className="dl-intro">
        Chore-row concepts on mock data — unlisted review page, not part of the app.
        Tap any check to feel completion (tap again to undo). Theme follows the system setting.
      </p>
      <nav className="dl-nav">
        {CONCEPTS.map((c) => (
          <a key={c.id} href={`#${c.id}`} className="choice-chip">{c.title}</a>
        ))}
        <Link to="/design-lab/widget" className="choice-chip">Widget lab →</Link>
      </nav>
      {CONCEPTS.map((c) => (
        <Concept key={c.id} id={c.id} title={c.title} blurb={c.blurb}>
          {c.component}
        </Concept>
      ))}
    </div>
  );
}
